import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse, HttpErrorResponse, HttpHeaders, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { throwError, BehaviorSubject, Observable } from 'rxjs';
import { catchError, tap, switchMap, filter, take } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { EncryptionHelper } from '../helper/encryption.helper';
import { LanguageService } from '../services/language.service';
import { environment } from '../../environments/environment';

// Interface para la respuesta estándar del middleware
interface MiddlewareResponse {
  success: boolean;
  reloadPermissions: boolean;
  idUser: number;
  token?: string;
  response: any;
}

// Variables para manejar el estado del interceptor (fuera de la función para persistir)
let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<any>(null);
let isReloadingPermissions = false;

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const accountService = inject(AccountService);
  const http = inject(HttpClient);
  const encryptionHelper = inject(EncryptionHelper);
  const router = inject(Router);

  // Agregar token a las peticiones si está disponible
  const authRequest = addTokenToRequest(req, accountService);

  return next(authRequest).pipe(
    // Procesar respuestas exitosas para verificar reloadPermissions
    tap(event => {
      if (event instanceof HttpResponse) {
        checkReloadPermissions(event, accountService, http, encryptionHelper, req.url);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handle401Error(authRequest, next, accountService, http, encryptionHelper, router);
      } else if (error.status === 403) {
        return handle403Error(error, accountService, http, encryptionHelper, router);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Agrega el token de autenticación a la petición
 */
function addTokenToRequest(request: any, accountService: AccountService): any {
  const token = accountService.getToken();
  const currentLanguage = LanguageService.getCurrentLanguage();

  // Solo agregar token si existe, no es una URL de autenticación y el token es válido
  if (token && !isAuthUrl(request.url) && isValidToken(token)) {
    // No establecer Content-Type si es FormData (para multipart/form-data)
    if (request.body instanceof FormData) {
      return request.clone({
        setHeaders: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': currentLanguage
        }
      });
    }
    
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept-Language': currentLanguage
      }
    });
  }

  // Si no hay token, agregar solo Accept-Language
  return request.clone({
    setHeaders: {
      'Accept-Language': currentLanguage
    }
  });
}

/**
 * Validar que el token tenga el formato correcto
 */
function isValidToken(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // Verificar que tenga al menos el formato básico de JWT (3 partes separadas por puntos)
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Verificar que no esté vacío
  return parts.every(part => part.length > 0);
}

/**
 * Maneja errores 401 (Unauthorized)
 * Directamente redirige al login sin intentar refresh
 */
function handle401Error(request: any, next: any, accountService: AccountService, http: HttpClient, encryptionHelper: EncryptionHelper, router: Router): Observable<any> {
  console.log('[AuthInterceptorFn] ⛔ 401 Unauthorized - Token expirado o inválido, cerrando sesión');
  router.navigate(['/login']);
  return throwError(() => new Error('Unauthorized - Token expired'));
}

/**
 * Maneja errores 403 (Forbidden)
 */
function handle403Error(error: HttpErrorResponse, accountService: AccountService, http: HttpClient, encryptionHelper: EncryptionHelper, router: Router): Observable<any> {
  if (isReloadingPermissions) {
    return throwError(() => error);
  }

  isReloadingPermissions = true;

  return reloadUserPermissions(accountService, http, encryptionHelper).pipe(
    switchMap(() => {
      isReloadingPermissions = false;
      
      setTimeout(() => {
        router.navigate(["/dashboard"]);
      }, 150);
      
      return throwError(() => error);
    }),
    catchError(permError => {
      console.error('Error al recargar permisos después de 403:', permError);
      isReloadingPermissions = false;
      return throwError(() => error);
    })
  );
}

/**
 * Refresca el token usando el sessionToken
 */
function refreshTokenWithSession(sessionToken: string, http: HttpClient): Observable<any> {
  const headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${sessionToken}`
  });

  return http.post<any>(`${environment.apiUrl}/auth/refresh`,
    { sessionToken },
    { headers }
  );
}

/**
 * Actualiza los tokens en localStorage
 */
function updateTokensInService(responseData: any): void {
  if (responseData.token) {
    localStorage.setItem('auth_token', responseData.token);
  }
  if (responseData.sessionToken) {
    localStorage.setItem('session_token', responseData.sessionToken);
  }
}

/**
 * Verifica si la respuesta indica que se deben recargar los permisos
 */
function checkReloadPermissions(event: HttpResponse<any>, accountService: AccountService, http: HttpClient, encryptionHelper: EncryptionHelper, url: string): void {
  if (isAuthUrl(url) || isPermissionsUrl(url)) {
    return;
  }

  if (isReloadingPermissions) {
    return;
  }

  try {
    const responseBody = event.body as MiddlewareResponse;

    if (responseBody && responseBody.reloadPermissions === true) {
      isReloadingPermissions = true;

      reloadUserPermissions(accountService, http, encryptionHelper).subscribe({
        next: (response) => {
          setTimeout(() => {
            window.location.reload();
          }, 150);
        },
        error: (error) => {
          isReloadingPermissions = false;
        }
      });
    }
  } catch (error) {
    isReloadingPermissions = false;
  }
}

/**
 * Recarga los permisos del usuario desde el servidor
 */
function reloadUserPermissions(accountService: AccountService, http: HttpClient, encryptionHelper: EncryptionHelper): Observable<any> {
  const token = accountService.getToken();

  if (!token) {
    return throwError(() => new Error('No hay token disponible para recargar permisos'));
  }

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  return http.get<MiddlewareResponse>(`${environment.apiUrl}menu/permissions`, { headers })
    .pipe(
      tap(response => {
        if (response.success && response.response) {
          updatePermissionsInService(response.response, accountService, encryptionHelper);

          if (response.token) {
            localStorage.setItem('auth_token', response.token);
          }
        }
      }),
      catchError(error => {
        console.error('Error al obtener permisos del usuario:', error);
        return throwError(() => error);
      })
    );
}

/**
 * Actualiza los permisos en el AccountService
 */
function updatePermissionsInService(permissionsData: any, accountService: AccountService, encryptionHelper: EncryptionHelper): void {
  const session = accountService.getSession();
  try {
    if (permissionsData.permissions) {
      encryptionHelper.setEncryptedItem('user_permissions', permissionsData.permissions);
      accountService.updatePermissions(permissionsData.permissions);
    }

    if(permissionsData.roleId) {
      if(!session) return;
      session.rol = permissionsData.roleId;
      accountService.setSession(session);
      accountService.updateUser(session);
   }

    if (permissionsData.user) {
      encryptionHelper.setEncryptedItem('user_info', permissionsData.user);
      accountService.updateUser(permissionsData.user);
    }
  } catch (error) {
    console.error('Error al actualizar permisos en localStorage:', error);
    throw error;
  }
}

/**
 * Verifica si la URL es del endpoint de permisos
 */
function isPermissionsUrl(url: string): boolean {
  return url.includes('/user-permissions') || url.includes('/menu/permissions');
}

/**
 * Verifica si la URL es de autenticación
 */
function isAuthUrl(url: string): boolean {
  if (!url) return false;

  const authPaths = [
    '/auth/login',
    '/auth/refresh',
    '/auth/logout',
    '/auth/forgot-password',
    '/auth/reset-password'
  ];

  return authPaths.some(path =>
    url.includes(path) ||
    url.endsWith(path) ||
    url.includes(`${environment.apiUrl}${path}`)
  );
}
